<template>
    <button
        class="fx-button"
        :class="{
            'kind--default': kind === 'default',
            'kind--link': kind === 'link',
            'kind--icon': kind === 'icon',
            'variant--default': variant === 'default',
            'variant--primary': variant === 'primary',
            'variant--accent': variant === 'accent',
            'variant--square': variant === 'square',
            'variant--frameless': variant === 'frameless',
            'variant--knockout': variant === 'knockout',
            'variant--fab': variant === 'fab',
            'size--small': size === 'small',
            'size--medium': size === 'medium',
            'size--large': size === 'large',
            loading: loading,
            active: active
        }"
        :aria-label="ariaLabel"
        :type="type"
        :disabled="disabled"
        @click="$emit('click')"
    >
        <span v-if="loading && (type === 'default' || kind === 'link')">
            {{ loadingCopy }}
        </span>
        <span v-else class="fx-button-content">
            <span v-if="icon" class="fx-button-icon" :class="'icon-' + icon"></span>
            <span class="fx-button-label" v-if="kind === 'default' || kind === 'link'">
                <slot>Button</slot>
            </span>
        </span>
    </button>
</template>

<script>
export default {
    name: 'fx-button',
    props: {
        active: Boolean,
        kind: {
            // default, link, icon
            type: String,
            default: 'default'
        },
        variant: {
            type: String,
            default: 'default'
            /*
                default: primary|accent
                link: default
                icon: default|square|frameless|knockout|fab
            */
        },
        size: {
            type: String,
            default: 'medium' // small|medium|large
        },
        icon: {
            type: String
        },
        loading: Boolean,
        ariaLabel: {
            type: String,
            default: 'Button'
        },
        disabled: Boolean,
        type: String,
        loadingCopy: {
            type: String,
            default: 'Loading...'
        }
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

:root {
    --transition-duration: 100ms;
}

.fx-button {
    background: transparent;
    border: none;
    outline-width: 0;
    padding: 0;
    cursor: pointer;
}

.kind--default {
    border-radius: var(--d-border-radius);
    font-weight: var(--fw-normal);
    background-color: white;
    transition: box-shadow var(--transition-duration) ease-in-out;
    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
    &:focus {
        box-shadow: var(--sh-focus);
    }
    &.loading {
        cursor: default;
    }
    &.size--small {
        padding: 4px 8px;
        font-size: var(--fs-small);
    }
    &.size--medium {
        padding: 8px 16px;
        font-size: var(--fs-normal);
    }
    &.size--large {
        padding: 12px 20px;
        font-size: var(--fs-large);
    }
    /* variants */
    &.variant--default {
        border: solid 1px var(--c-primary);
        color: var(--c-primary);
        &:disabled,
        &.loading {
            background: white !important;
        }
        &:hover {
            background: linear-gradient(180deg, white 0%, var(--c-primary-light) 100%);
        }
        &:active,
        &.active {
            background: var(--c-primary-light);
        }
    }
    &.variant--primary {
        background-color: var(--c-primary);
        color: white;
        &:disabled,
        &.loading {
            background: var(--c-primary) !important;
        }
        &:hover {
            background: linear-gradient(180deg, var(--c-primary) 0%, var(--c-primary-dark) 100%);
        }
        &:active,
        &.active {
            background: var(--c-primary-dark);
        }
    }
    &.variant--accent {
        background-color: var(--c-accent);
        color: white;
        &:disabled,
        &.loading {
            background: var(--c-button-disabled) !important;
            border: 1px solid var(--c-button-disabled);
            color: var(--c-gray-4);
        }
        &:hover {
            background: linear-gradient(180deg, var(--c-accent-light) 0%, var(--c-accent) 100%);
        }
        &:active,
        &.active {
            background: var(--c-accent-light);
        }
    }
}

.kind--link {
    background: transparent;
    border: none;
    padding: 2px;
    cursor: pointer;
    font-weight: var(--fw-normal);
    border-bottom: solid 2px transparent;
    color: var(--c-link);
    transition: border-color 100ms ease-in-out;
    &:hover,
    &:focus {
        box-shadow: none;
        border-color: var(--c-link);
    }
    &:active,
    &.active {
        transition: none;
        color: var(--c-interact-active);
        border-color: var(--c-interact-active);
    }
    &:disabled {
        cursor: default;
        opacity: 0.5;
        border: none;
    }
    &.loading {
        cursor: default;
        border: none;
        &:active {
            border-color: var(--c-link);
            color: var(--c-link);
        }
    }

    /* sizes */
    &.size--small {
        font-size: var(--fs-small);
    }
    &.size--medium {
        font-size: var(--fs-normal);
    }
    &.size--large {
        font-size: var(--fs-large);
    }
}

.kind--icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: solid 1px transparent;

    /* variants */
    &.variant--default,
    &.variant--square,
    &.variant--frameless,
    &.variant--knockout {
        &.size--small {
            width: 20px;
            height: 20px;
            font-size: var(--fs-small);
        }
        &.size--medium {
            width: 24px;
            height: 24px;
            font-size: var(--fs-normal);
        }
        &.size--large {
            width: 32px;
            height: 32px;
            font-size: var(--fs-larger);
        }
    }
    &.variant--default {
        background: white;
        box-shadow: var(--sh-normal);
        color: var(--c-gray-4);
        border-radius: 100%;
        &:hover {
            border: solid 1px var(--c-gray-9);
            background: linear-gradient(180deg, white 0%, var(--c-gray-9) 100%);
        }
        &:focus {
            border-color: var(--c-primary);
            box-shadow: var(--sh-focus);
        }
        &:active,
        &.active {
            background: var(--c-gray-9);
        }
        &:disabled {
            box-shadow: none;
            border-color: var(--c-gray-9);
            color: var(--c-interact-disabled);
            &:hover {
                background: white;
            }
        }
    }
    &.variant--square {
        background: white;
        box-shadow: var(--sh-normal);
        color: var(--c-gray-4);
        border-radius: var(--d-border-radius);
        &:hover {
            border-color: var(--c-gray-9);
            background: linear-gradient(180deg, white 0%, var(--c-gray-9) 100%);
        }
        &:focus {
            border-color: var(--c-primary);
            box-shadow: var(--sh-focus);
        }
        &:active,
        &.active {
            background: var(--c-gray-9);
        }
        &:disabled {
            box-shadow: none;
            border-color: var(--c-gray-9);
            color: var(--c-interact-disabled);
            &:hover {
                background: white;
            }
        }
    }
    &.variant--frameless {
        border-radius: 100%;
        color: var(--c-primary);
        background: transparent;
        box-shadow: none;
        &:hover {
            border-color: var(--c-primary);
        }
        &:focus {
            box-shadow: none;
            border-color: var(--c-interact-focus);
        }
        &:active,
        &.active {
            color: var(--c-interact-active);
        }
    }
    &.variant--knockout {
        border-radius: 100%;
        color: white;
        background: transparent;
        box-shadow: none;
        &:hover {
            border-color: white;
        }
        &:focus {
            box-shadow: none;
            border-color: var(--c-interact-focus);
        }
        &:active,
        &.active {
            color: var(--c-interact-active);
        }
    }
    &.variant--fab {
        width: 42px;
        height: 42px;
        border-radius: 100%;
        background-color: var(--c-accent);
        color: white;
        box-shadow: var(--sh-normal);
    }
}

.fx-button-content {
    display: flex;
    align-items: center;
    justify-content: center;
}

.fx-button-icon {
    display: inline-flex;
}

.fx-button-icon + .fx-button-label {
    margin-left: 2px;
}
</style>
